import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Companies, Items, Offices, Samples } from '@etoh/database/angular';
import { UserService } from 'apps/desktop/src/app/user.service';
import copy from 'fast-copy';

import {
  combineLatest,
  firstValueFrom,
  flatMap,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { BackButtonComponent } from '../../../shared/components/layout/back-button/back-button.component';
import { PdfUserGeneratorComponent } from '../../../shared/components/pdf/pdf-user-generator/pdf-user-generator.component';
import { PdfFormat } from '../../../shared/interfaces/pdfGenerators.interface';
import { StoreService } from '../../../store.service';
import { UserOfficeService } from '../../../user-office.service';
import { SampleLabelPdfContentComponent } from '../../components/sample-label-pdf-content/sample-label-pdf-content.component';
import { SampleTemplateName } from '../../sample.interfaces';

@Component({
  selector: 'etoh-generate-sample-label-pdf',
  templateUrl: './generate-sample-label-pdf.component.html',
  styleUrls: ['./generate-sample-label-pdf.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    BackButtonComponent,
    RouterModule,
    NzRadioModule,
    ReactiveFormsModule,
    PdfUserGeneratorComponent,
    FlexLayoutModule,
    SampleLabelPdfContentComponent,
  ],
})
export class GenerateSampleLabelPdfComponent implements OnInit {
  selectedTemplate = new FormControl<SampleTemplateName>(
    this.userService.getJsonField(
      'savedFormFields',
      'pdfSampleLabelTemplate'
    ) ?? 'France'
  );

  samplesId: number[];

  samples$: Observable<Samples[]>;

  standalone = true;

  formatOptions: PdfFormat<SampleTemplateName>[] = [
    {
      name: 'France',
      value: {
        width: '105mm',
        height: '148mm',
      },
    },
    {
      name: 'USA',
      value: {
        width: '101.6mm',
        height: '127mm',
      },
    },
    {
      name: 'Australia',
      value: {
        width: '95mm',
        height: '95mm',
      },
    },
    {
      name: 'South Africa',
      value: {
        width: '99mm',
        height: '67.7mm',
      },
    },
  ];

  formats: PdfFormat<string | null>[];

  destroy$ = new Subject();

  get pdfName() {
    return `sample-label-${this.samplesId.join('-')}`;
  }

  data$: Observable<any>;

  constructor(
    private cd: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private storeService: StoreService,
    private router: Router,
    private userService: UserService,
    public userOfficeService: UserOfficeService
  ) {}

  ngOnInit(): void {
    if (!this.samplesId?.length) {
      const sampleId = +this.activatedRoute.snapshot.params['id'];

      this.samplesId = [sampleId];
    }

    this.selectedTemplate.valueChanges
      .pipe(startWith(this.selectedTemplate.value), takeUntil(this.destroy$))
      .subscribe(() => {
        this.userService.updateJsonbField(
          'savedFormFields',
          'pdfSampleLabelTemplate',
          this.selectedTemplate.value
        );
        this.setFormat();
      });

    this.samples$ = this.storeService.getStoreByKeys$<Samples>('samples').pipe(
      take(1),
      flatMap(async (samplesByKeys) => {
        samplesByKeys = copy(samplesByKeys);

        let samples = this.samplesId.map((id) => samplesByKeys[id]);

        const itemsByKeys = await firstValueFrom(
          this.storeService.getStoreByKeys$<Items>('items')
        );

        const companiesByKeys = await firstValueFrom(
          this.storeService.getStoreByKeys$<Companies>('companies')
        );

        const officesByKeys = await firstValueFrom(
          this.storeService.getStoreByKeys$<Offices>('offices')
        );

        samples = samples.map((sample) => {
          const item = copy(itemsByKeys[sample.itemId]);

          const office = officesByKeys?.[(sample as any).officeId];

          if (item?.supplierCompanyId) {
            item.supplierCompany = companiesByKeys[item.supplierCompanyId];
          }

          return {
            ...sample,
            item: item,
            office: office,
          };
        });

        return samples;
      })
    );

    this.data$ = combineLatest([
      this.samples$,
      this.selectedTemplate.valueChanges.pipe(
        startWith(this.selectedTemplate.value)
      ),
    ]).pipe(
      map(([samples, templateName]) => {
        return {
          samples,
          templateName,
        };
      }),
      shareReplay(1)
    );
  }

  private setFormat() {
    const f = this.formatOptions.find((format) => {
      return format.name === this.selectedTemplate.value;
    });

    if (!f) {
      this.formats = [];
    } else {
      this.formats = [
        {
          ...f,
          name: null,
        },
      ];
    }
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
